import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost } from '../../utils/api';
import { normalizeReviewHtmlImages } from '../../utils/reviewImage';
import { sanitizeReviewHtml } from '../../utils/reviewHtml';
import { usePageTitle } from '../../contexts/PageTitleContext';
import '../../css/Review/review.css';

const formatDateTime = (raw) => {
  if (!raw) return '';
  const normalized = String(raw).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(raw);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}.${month}.${day} ${hour}:${minute}`;
};

export default function ReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setPageTitle } = usePageTitle();

  const [review, setReview] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [openReplyId, setOpenReplyId] = useState(null);
  const [likePending, setLikePending] = useState(false);
  const [submitPending, setSubmitPending] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  const [actionError, setActionError] = useState('');
  const activeId = useRef(id);
  const requestVersion = useRef(0);
  const mutation = useRef(null);
  activeId.current = id;

  const pageParam = Number(searchParams.get('page'));
  const pageSuffix = Number.isInteger(pageParam) && pageParam > 1 ? `?page=${pageParam}` : '';

  const loadDetail = useCallback(() => {
    if (!id) return;
    const version = ++requestVersion.current;
    setIsLoading(true);
    setError('');
    return apiGet('/review/get_review.php', { id })
      .then((res) => {
        if (activeId.current !== id || requestVersion.current !== version) return;
        if (res?.success) {
          setReview(res.data || null);
          setComments(Array.isArray(res.comments) ? res.comments : []);
          setCsrfToken(res.csrf_token || '');
          const title = res?.data?.title ? `${res.data.title} | 고객 후기` : '고객 후기 상세';
          setPageTitle(title);
        } else {
          setError(res?.message || '리뷰를 불러오지 못했습니다.');
          setReview(null);
          setComments([]);
        }
      })
      .catch((err) => {
        if (activeId.current !== id || requestVersion.current !== version) return;
        setError(err?.response?.data?.message || '리뷰를 불러오지 못했습니다.');
        setReview(null);
        setComments([]);
      })
      .finally(() => {
        if (activeId.current === id && requestVersion.current === version) setIsLoading(false);
      });
  }, [id, setPageTitle]);

  useEffect(() => {
    setReview(null);
    setComments([]);
    setCsrfToken('');
    setActionError('');
    setNewComment('');
    setReplyDrafts({});
    setOpenReplyId(null);
    setLikePending(false);
    setSubmitPending(false);
    setDeletePending(false);
    mutation.current = null;
    loadDetail();
    return () => { requestVersion.current += 1; mutation.current = null; };
  }, [loadDetail]);

  const startMutation = () => {
    if (!review || String(review.id) !== String(id) || mutation.current) return null;
    if (!csrfToken) {
      setActionError('인증 정보를 확인할 수 없습니다. 페이지를 새로고침한 후 다시 시도해주세요.');
      return null;
    }
    const ticket = { id };
    mutation.current = ticket;
    setActionError('');
    return ticket;
  };
  const isCurrentMutation = (ticket) => activeId.current === ticket.id && mutation.current === ticket;
  const finishMutation = (ticket) => {
    if (!isCurrentMutation(ticket)) return;
    mutation.current = null;
    setLikePending(false);
    setSubmitPending(false);
    setDeletePending(false);
  };
  const mutationError = (err, fallback) => err?.response?.status === 409
    ? '리뷰가 다른 곳에서 변경되었습니다. 페이지를 새로고침한 후 다시 확인해주세요.'
    : err?.response?.status === 403
      ? '요청 권한 또는 인증 정보가 변경되었습니다. 페이지를 새로고침한 후 다시 확인해주세요.'
    : err?.response?.data?.message || fallback;

  const onToggleLike = async () => {
    if (review?.is_visible === false) return;
    const ticket = startMutation();
    if (!ticket) return;
    setLikePending(true);
    try {
      const res = await apiPost('/review/toggle_review_like.php', { review_id: review.id }, { headers: { 'X-CSRF-Token': csrfToken } });
      if (!isCurrentMutation(ticket)) return;
      if (res?.success) {
        setReview((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            my_liked: !!res.liked,
            like_count: Number(res.like_count || 0),
          };
        });
      } else {
        alert(res?.message || '좋아요 처리에 실패했습니다.');
      }
    } catch (err) {
      if (isCurrentMutation(ticket)) setActionError(mutationError(err, '좋아요 처리에 실패했습니다.'));
    } finally {
      finishMutation(ticket);
    }
  };

  const submitComment = async (parentCommentId = null) => {
    if (review?.is_visible === false) return;
    const content = parentCommentId ? (replyDrafts[parentCommentId] || '').trim() : newComment.trim();
    if (!content) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }
    const ticket = startMutation();
    if (!ticket) return;
    setSubmitPending(true);
    try {
      const res = await apiPost('/review/create_review_comment.php', {
        review_id: review.id,
        parent_comment_id: parentCommentId,
        content,
      }, { headers: { 'X-CSRF-Token': csrfToken } });
      if (!isCurrentMutation(ticket)) return;
      if (res?.success) {
        if (parentCommentId) {
          setReplyDrafts((prev) => ({ ...prev, [parentCommentId]: '' }));
          setOpenReplyId(null);
        } else {
          setNewComment('');
        }
        await loadDetail();
      } else {
        alert(res?.message || '댓글 등록에 실패했습니다.');
      }
    } catch (err) {
      if (isCurrentMutation(ticket)) setActionError(mutationError(err, '댓글 등록에 실패했습니다.'));
    } finally {
      finishMutation(ticket);
    }
  };

  const onDelete = async () => {
    if (!review?.can_delete || mutation.current) return;
    if (!window.confirm('이 리뷰를 삭제하시겠습니까? 삭제하면 더 이상 표시되지 않으며, 같은 예약으로 직접 다시 작성할 수 없습니다. 복구·재작성은 고객센터에 문의해주세요.')) return;
    const ticket = startMutation();
    if (!ticket) return;
    setDeletePending(true);
    try {
      const res = await apiPost('/review/delete_review.php', {
        id: review.id,
        revision_token: review.revision_token,
      }, { headers: { 'X-CSRF-Token': csrfToken } });
      if (!isCurrentMutation(ticket)) return;
      if (res?.success) navigate(`/review${pageSuffix}`, { replace: true });
      else setActionError(res?.message || '리뷰 삭제에 실패했습니다.');
    } catch (err) {
      if (isCurrentMutation(ticket)) setActionError(mutationError(err, '리뷰 삭제에 실패했습니다.'));
    } finally {
      finishMutation(ticket);
    }
  };

  const actionPending = likePending || submitPending || deletePending;

  const summaryText = useMemo(() => {
    const cruise = review?.cruise;
    if (!cruise) return '';
    const nights = Number(cruise.nights || 0);
    const periodLabel = nights > 0 ? `${nights}박 ${nights + 1}일` : '';
    const dep = String(cruise?.departure_port_name || '').trim();
    const arr = String(cruise?.arrival_port_name || '').trim();
    const route = [dep, arr].filter(Boolean).join(' - ');
    return [cruise.name, route, cruise.saildate, cruise.enddate, periodLabel].filter(Boolean).join(' | ');
  }, [review]);

  const normalizedContentHtml = useMemo(
    () => sanitizeReviewHtml(normalizeReviewHtmlImages(review?.content_html || '')),
    [review?.content_html]
  );

  const renderCommentNode = (comment) => {
    const depth = Number(comment?.depth || 0);
    const commentId = Number(comment?.id || 0);
    const canReply = !!comment?.can_reply && review?.is_visible !== false;
    const replyValue = replyDrafts[commentId] || '';

    return (
      <div
        key={commentId}
        className="review-comment-item"
        style={{ marginLeft: `${Math.min(depth, 4) * 18}px` }}
      >
        <div className="review-comment-author">{comment?.author_name || '익명'}</div>
        <div className="review-comment-content">{comment?.content || ''}</div>
        <div className="review-comment-meta">
          <span>{formatDateTime(comment?.created_at)}</span>
          {canReply && (
            <button
              type="button"
              className="review-link-btn"
              onClick={() => setOpenReplyId((prev) => (prev === commentId ? null : commentId))}
            >
              답글달기
            </button>
          )}
        </div>

        {canReply && openReplyId === commentId && (
          <div className="review-reply-form">
            <input
              type="text"
              value={replyValue}
              onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [commentId]: e.target.value }))}
              placeholder="답글을 입력해주세요."
              maxLength={2000}
            />
            <button
              type="button"
              className="review-submit-btn"
              onClick={() => submitComment(commentId)}
              disabled={actionPending}
            >
              등록
            </button>
          </div>
        )}

        {Array.isArray(comment?.children) && comment.children.length > 0 && (
          <div className="review-comment-children">
            {comment.children.map((child) => renderCommentNode(child))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading || (review && String(review.id) !== String(id))) {
    return (
      <div className="review-page">
        <div className="review-container">
          <div className="review-status">로딩 중...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="review-page">
        <div className="review-container">
          <div className="review-status error">{error}</div>
          <div className="review-detail-actions">
            <button type="button" className="review-line-btn" onClick={() => navigate(`/review${pageSuffix}`)}>
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="review-page">
        <div className="review-container">
          <div className="review-status">리뷰를 찾을 수 없습니다.</div>
          <div className="review-detail-actions">
            <button type="button" className="review-line-btn" onClick={() => navigate(`/review${pageSuffix}`)}>
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="review-page">
      <div className="review-container">
        <button type="button" className="review-line-btn" onClick={() => navigate(`/review${pageSuffix}`)}>
          목록으로
        </button>

        <div className="review-detail-head">
          <h2 className="review-detail-title">{review.title}</h2>
          <div className="review-detail-meta">
            <span>{review.author_name}</span>
            <span>{formatDateTime(review.published_at)}</span>
            <span>조회 {Number(review.view_count || 0)}</span>
          </div>
          {summaryText && <div className="review-cruise-summary">{summaryText}</div>}
        </div>

        {review.is_visible === false && (
          <div className="review-status">관리자에 의해 비공개 처리된 리뷰입니다. 내용을 수정해도 공개 상태로 변경되지 않습니다.</div>
        )}
        {(review.can_edit === true || review.can_delete === true) && (
          <div className="review-detail-actions review-owner-actions">
            {review.can_edit === true && <button type="button" className="review-line-btn" disabled={actionPending} onClick={() => navigate(`/review/${id}/edit${pageSuffix}`)}>리뷰 수정</button>}
            {review.can_delete === true && <button type="button" className="review-line-btn review-delete-btn" disabled={actionPending} onClick={onDelete}>{deletePending ? '삭제 중...' : '리뷰 삭제'}</button>}
          </div>
        )}
        {actionError && <div className="review-status error" role="alert">{actionError}</div>}

        <div className="review-like-row">
          <button
            type="button"
            className={`review-like-btn ${review.my_liked ? 'active' : ''}`}
            onClick={onToggleLike}
            disabled={actionPending || review.is_visible === false}
          >
            좋아요 {Number(review.like_count || 0)}
          </button>
          <span>댓글 {Number(review.comment_count || 0)}</span>
        </div>

        <div className="review-content" dangerouslySetInnerHTML={{ __html: normalizedContentHtml }} />

        <div className="review-comment-section">
          {review.is_visible !== false && <div className="review-comment-input-row">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="댓글을 입력해주세요."
              maxLength={2000}
            />
            <button
              type="button"
              className="review-submit-btn"
              onClick={() => submitComment(null)}
              disabled={actionPending}
            >
              등록
            </button>
          </div>}

          {comments.length === 0 ? (
            <div className="review-comment-empty">등록된 댓글이 없습니다.</div>
          ) : (
            <div className="review-comment-list">
              {comments.map((comment) => renderCommentNode(comment))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
